/**
 * API Client
 * Configured axios instance for API calls.
 */

import axios, {
  type AxiosInstance,
  type AxiosError,
  type InternalAxiosRequestConfig,
  type AxiosResponse,
} from 'axios';
import { logger } from '@utils/logger';
import { ApiError, NetworkError, isNetworkError } from '@utils/errorHandler';
import { STORAGE_KEYS, API_TIMEOUT } from '@config/constants';
import { env } from '@config/env';
import { useAuthStore } from '@store/authStore';

/**
 * API configuration.
 * Uses environment config which defaults to http://dream.test/api for Herd/Laravel backend
 */
const API_CONFIG = {
  baseURL: env.API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
};

/**
 * Creates and configures the axios instance.
 */
const createApiClient = (): AxiosInstance => {
  const client = axios.create(API_CONFIG);

  // Request interceptor
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // For FormData, remove Content-Type header to let axios set it with boundary
      if (config.data instanceof FormData && config.headers) {
        delete config.headers['Content-Type'];
      }

      // Add user timezone for date/time parsing (e.g. scheduled memos)
      const timezone = Intl.DateTimeFormat?.().resolvedOptions?.().timeZone || '';
      if (timezone && config.headers) {
        config.headers['X-Timezone'] = timezone;
      }

      // Add auth token if available
      const getAuthToken = (): string | null => {
        // First try Zustand store
        try {
          const authState = useAuthStore.getState();
          if (authState.token) {
            return authState.token;
          }
        } catch (e) {
          // Store not available, continue to localStorage
        }

        // Fallback to localStorage
        const stored = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
        if (!stored) return null;

        // Zustand persist stores as: { state: { user, token, isAuthenticated }, version: 0 }
        try {
          const parsed = JSON.parse(stored);
          return parsed?.state?.token || parsed?.token || null;
        } catch {
          // If not JSON, it might be stored as plain string (from LoginPage direct setItem)
          return stored;
        }
      };

      const token = getAuthToken();
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Add CSRF token if available
      const csrfToken = getCsrfToken();
      if (csrfToken && config.headers) {
        config.headers['X-CSRF-TOKEN'] = csrfToken;
      }

      // Log request in development
      logger.debug(`API Request: ${config.method?.toUpperCase()} ${config.url}`, {
        params: config.params,
        data: config.data instanceof FormData ? '[FormData]' : config.data,
      });

      return config;
    },
    (error: AxiosError) => {
      logger.error('API Request Error', error);
      return Promise.reject(error);
    }
  );

  // Response interceptor
  client.interceptors.response.use(
    (response: AxiosResponse) => {
      logger.debug(`API Response: ${response.status} ${response.config.url}`, {
        data: response.data,
      });
      return response;
    },
    async (error: AxiosError) => {
      // Handle network errors
      if (isNetworkError(error)) {
        logger.error('Network Error', error);
        return Promise.reject(new NetworkError());
      }

      const originalRequest = error.config as InternalAxiosRequestConfig & {
        _retry?: boolean;
      };

      // Handle 401 Unauthorized - attempt token refresh
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          // TODO: Implement token refresh logic
          // const newToken = await refreshToken();
          // localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, newToken);
          // originalRequest.headers.Authorization = `Bearer ${newToken}`;
          // return client(originalRequest);
        } catch (refreshError) {
          // Clear auth and redirect to login
          localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
          localStorage.removeItem(STORAGE_KEYS.USER_DATA);
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }

      // Transform to ApiError
      const apiError = ApiError.fromResponse(error);
      logger.error('API Error', apiError);
      return Promise.reject(apiError);
    }
  );

  return client;
};

/**
 * Gets CSRF token from meta tag or cookie.
 */
const getCsrfToken = (): string | null => {
  // Try meta tag first
  const metaTag = document.querySelector('meta[name="csrf-token"]');
  if (metaTag) {
    return metaTag.getAttribute('content');
  }

  // Try cookie
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'XSRF-TOKEN') {
      return decodeURIComponent(value);
    }
  }

  return null;
};

/**
 * The configured API client instance.
 */
export const apiClient = createApiClient();

/**
 * Helper methods for common HTTP methods.
 */
export const api = {
  get: <T>(url: string, config?: object) =>
    apiClient.get<T>(url, config).then((res) => res.data),

  post: <T>(url: string, data?: object, config?: object) => {
    const { impersonation } = useAuthStore.getState();
    if (impersonation) {
      // Allow logout while impersonating
      if (url.startsWith('/auth/logout')) {
        return apiClient.post<T>(url, data, config).then((res) => res.data);
      }
      if (url.startsWith('/auth/impersonate')) {
        return Promise.reject(new Error('Exit read-only mode before switching accounts.'));
      }
      return Promise.reject(new Error('Read-only mode: changes are not allowed.'));
    }
    return apiClient.post<T>(url, data, config).then((res) => res.data);
  },

  put: <T>(url: string, data?: object, config?: object) => {
    const { impersonation } = useAuthStore.getState();
    if (impersonation) return Promise.reject(new Error('Read-only mode: changes are not allowed.'));
    return apiClient.put<T>(url, data, config).then((res) => res.data);
  },

  patch: <T>(url: string, data?: object, config?: object) => {
    const { impersonation } = useAuthStore.getState();
    if (impersonation) return Promise.reject(new Error('Read-only mode: changes are not allowed.'));
    return apiClient.patch<T>(url, data, config).then((res) => res.data);
  },

  delete: <T>(url: string, config?: object) => {
    const { impersonation } = useAuthStore.getState();
    if (impersonation) return Promise.reject(new Error('Read-only mode: changes are not allowed.'));
    return apiClient.delete<T>(url, config).then((res) => res.data);
  },
};

export default apiClient;

